import * as React from 'react';
import { observable, action, computed } from 'mobx';
import { diContainer, IServiceInfo } from '~setup/diContainer';
import serviceNames from '~setup/VtoService';
import { ELCLogger, TriggerType } from '@estee/elc-logging';
import {
    ICrossSellCategory,
    ICrossSellShades,
    IProduct,
    IShade,
    ISku,
    IVtoEventListenerResult,
    IVtoFoundationPairShadeLabels,
    IVtoFoundationPerfectMatches,
    IVtoFoundationSaveMatch,
    IVtoFoundationShadeLabels,
    IVtoServiceConfig
} from '~interfaces/IVto';
import { IVtoTranslations } from '~exported/vto/Vto';
import { emit, events, on, query, serviceQueries } from '@estee/elc-service-bus';
import { BrowserStorage } from '@estee/elc-universal-utils';
import * as VtoConstants from '~constants/Vto';
import { VtoBaseController, IVtoWindow } from './VtoBaseController';
import { PersonalizationRepository } from '~repositories/PersonalizationRepository';
import { UserInfoRepository } from '~repositories/UserInfoRepository';
import { setVtoMatchCookie } from '~utils/setVtoMatchCookie';
import { getVtoMatchCookie } from '~utils/getVtoMatchCookie';
import { SurveyUseCase } from '../use-cases/SurveyUseCase';
import { PERLGEM_USER_ID, SESSION_ID, surveyTypes } from '~constants/Survey';
import { setVtoSurveyStorage } from '~utils/setVtoSurveyStorage';
import { getVtoSurveyStorage } from '~utils/getVtoSurveyStorage';
import { ISurveyFields } from '~interfaces/ISurvey';
import { CLOSE_SURVEY_TIMEOUT } from '~constants/VtoSurvey';
import { SurveyRepository } from '~repositories/SurveyRepository';
import { ISurveyData } from '~api/SurveyApiSdk';

declare let window: IVtoWindow;

export interface IVtoInitializeController {
    config: IVtoServiceConfig;
    vtoIframeRef: React.RefObject<HTMLDivElement>;
    translations: IVtoTranslations;
}

export class VtoViewController extends VtoBaseController {
    @observable public isVtoShadeGridEnabled: boolean = false;
    @observable public isVtoLoaded: boolean = false;
    @observable public isVtoFoundationOverlayOpened: boolean = false;
    @observable public selectedPairSkuId: string;
    @observable public selectedPairShadeName: string = '';
    @observable private selectedSku: ISku;
    @observable public activeTab: string = VtoConstants.foundationTabs.perfectShadesTab;
    @observable public isMatchSaved: boolean = false;
    @observable public isSaveInProgress: boolean = false;
    @observable public isSpinnerEnabled: boolean = true;
    @observable private showProductImageSection: boolean = false;
    @observable public crossSellSkuData: ISku[];
    @observable public isVtoFoundationQrCodeOverlayEnabled: boolean = false;
    @observable public isSurveyVisible: boolean = false;
    @observable public showThankMessage: boolean = false;
    @observable public surveyButtonIndex: number | null;
    @observable public surveyComment: string = '';
    @observable public savedMatch: string = '';

    public translations: IVtoTranslations;
    private togglePerfectShades?: (hidden: boolean) => void;
    private vtoReInitTimeout: number;
    private vtoIframeRef: React.RefObject<HTMLDivElement>;
    private vtoShadeResult?: IVtoFoundationPerfectMatches;
    private isShadeSidebarVtoOpened: boolean = false;
    private personalizationRepository: PersonalizationRepository;
    private userInfoRepository: UserInfoRepository;
    private payloadEventValueCameraStart: number;
    private payloadEventValueCameraEnd: number;
    private vtoType: string;
    private logger: ELCLogger;
    private surveyRepository: SurveyRepository;
    private isSurveySubmitted: boolean = false;
    private surveyViewed: boolean = false;

    constructor(serviceInfo: IServiceInfo) {
        super();
        this.checkVtoDisclose();
        this.personalizationRepository = diContainer.get(serviceNames.personalizationApiSdk);
        this.userInfoRepository = diContainer.get(serviceNames.userInfoRepository);
        this.surveyRepository = diContainer.get(serviceNames.surveyRepository);
        this.logger = new ELCLogger(serviceInfo);
    }

    public checkHidePriceOnLoad = () => {
        this.checkVtoHidePriceParam();

        if (this.isVtoPriceHidden) {
            emit(events.VTO_PRICE_SUPPRESSED, { vtoType: this.vtoType });
        }
    };

    @action
    public initialize = (props: IVtoInitializeController) => {
        const { config, translations, vtoIframeRef } = props;
        const {
            vtoConfig,
            isVtoMakeupEnabled,
            isVtoFoundationEnabled,
            selectedSku,
            productIdList,
            hasShadeSidebarVto,
            togglePerfectShades
        } = config;
        this.vtoConfig = vtoConfig;
        this.isVtoMakeupEnabled = !!isVtoMakeupEnabled;
        this.isVtoFoundationEnabled = !!isVtoFoundationEnabled;
        this.isVtoFoundationMultiIds = !!this.isVtoFoundationMultiIdsEnabled;
        this.selectedSku = selectedSku;
        this.product = selectedSku.parentProduct;
        this.productIdList = productIdList || [this.product.productId];
        this.translations = translations;
        this.vtoIframeRef = vtoIframeRef;
        this.vtoFoundationPerfectMatchText = this.getVtoPerfectMatchText();
        this.hasShadeSidebarVto = hasShadeSidebarVto;
        this.showProductImageSection = !!(
            this.vtoMakeupFeature && this.vtoMakeupFeature.showProductImageSection
        );
        this.vtoType = this.getVtoType();

        this.togglePerfectShades = togglePerfectShades;

        this.enableProductImage(true);

        this.loadCrossSellProducts();
        this.addEventListeners(VtoConstants.VtoEvents, this.handleEventListenerCallbacks);
        this.openShadeSidebarVto();
    };

    @computed
    get data() {
        return {
            isVtoShadeGridEnabled: this.isVtoShadeGridEnabled,
            isVtoFoundationOverlayOpened: this.isVtoFoundationOverlayOpened,
            isVtoOpened: this.isVtoOpened,
            isVtoPriceHidden: this.isVtoPriceHidden,
            activeTabs: this.activeTabContent,
            isMatchSaved: this.isMatchSaved,
            isSaveInProgress: this.isSaveInProgress,
            isSpinnerEnabled: this.isSpinnerEnabled,
            isSupportedBrowserOverlayVisible: this.isSupportedBrowserOverlayVisible,
            isSupportedBrowserOverlayOpen: this.isSupportedBrowserOverlayOpen,
            selectedCrossSellShades: this.selectedCrossSellShades,
            selectedCrossSellShade: this.selectedCrossSellShade,
            selectedCrossSellSku: this.selectedCrossSellSku,
            selectedCrossSellProductUrl: this.selectedCrossSellProductUrl,
            selectedCrossSellLargeImages: this.selectedCrossSellLargeImages,
            selectedCrossSellSizeValue: this.selectedCrossSellSizeValue,
            foundationPairText: this.foundationPairText,
            isVtoFoundationQrCodeOverlayEnabled: this.isVtoFoundationQrCodeOverlayEnabled,
            isSurveyVisible: this.isSurveyVisible,
            showThankMessage: this.showThankMessage,
            surveyButtonIndex: this.surveyButtonIndex,
            surveyComment: this.surveyComment,
            savedMatch: this.savedMatch
        };
    }

    private getVtoPerfectMatchText = () => {
        return Object.values(VtoConstants.FoundationShadeTypes).reduce((acc, value) => {
            const key = `${value}Label` as keyof IVtoTranslations;

            return {
                ...acc,
                [value]: this.translations[key]
            };
        }, {} as IVtoFoundationShadeLabels);
    };

    public getVtoType = () => {
        if (this.isVtoMakeupEnabled) {
            return VtoConstants.VtoTypes.MAKEUP;
        }

        return VtoConstants.VtoTypes.FOUNDATION;
    };

    private handleEventListenerCallbacks: {
        [key in VtoConstants.VtoEvents]: (result: IVtoEventListenerResult) => void;
    } = {
        [VtoConstants.VtoEvents.LOADING]: (percentage) => this.handleVtoLoadingEvent(percentage),
        [VtoConstants.VtoEvents.LOADED]: () => this.handleVtoLoadedEvent(),
        [VtoConstants.VtoEvents.OPENED]: () => this.handleVtoOpenedEvent(),
        [VtoConstants.VtoEvents.CLOSED]: () => this.handleVtoClosedEvent(),
        [VtoConstants.VtoEvents.SKIN_SHADE_FOUND]: (result) => {
            this.handleVtoSkinShadeFoundEvent(result);
            this.handleVtoResize(false);
        },
        [VtoConstants.VtoEvents.CAMERA_CLOSED]: () =>
            emit(events.VTO_CAMERA_CLOSED, { vtoType: this.vtoType }),
        [VtoConstants.VtoEvents.CAMERA_FAILED]: () =>
            emit(events.VTO_CAMERA_FAILED, { vtoType: this.vtoType }),
        [VtoConstants.VtoEvents.CAMERA_OPENED]: () => this.handleCameraOpenedEvent(),
        [VtoConstants.VtoEvents.COMPARE]: () => emit(events.VTO_COMPARE, { vtoType: this.vtoType }),
        [VtoConstants.VtoEvents.COMPARE_DISABLED]: () =>
            emit(events.VTO_COMPARE_DISABLED, { vtoType: this.vtoType }),
        [VtoConstants.VtoEvents.ENGINE_CLOSED]: () =>
            emit(events.VTO_ENGINE_CLOSED, { vtoType: this.vtoType }),
        [VtoConstants.VtoEvents.KISS_DETECTED]: () =>
            emit(events.VTO_KISS_DETECTED, { vtoType: this.vtoType }),
        [VtoConstants.VtoEvents.PHOTO_LOADED]: () =>
            emit(events.VTO_PHOTO_LOADED, { vtoType: this.vtoType }),
        [VtoConstants.VtoEvents.PHOTO_SAVED]: () =>
            emit(events.VTO_PHOTO_SAVED, { vtoType: this.vtoType }),
        [VtoConstants.VtoEvents.RESET]: () => {
            emit(events.VTO_RESET, { vtoType: this.vtoType });
            this.handleVtoResize(true);
        },
        [VtoConstants.VtoEvents.VIDEO_LOADED]: () =>
            emit(events.VTO_VIDEO_LOADED, { vtoType: this.vtoType }),
        [VtoConstants.VtoEvents.SHADE_FINDER_STARTED]: () =>
            this.handleVtoShadeFinderStartedEvent(),
        [VtoConstants.VtoEvents.SHADE_FINDER_COMPLETED]: () => {
            emit(events.VTO_SHADE_FINDER_COMPLETED, { vtoType: this.vtoType });
            this.handleShadeFinderCompleteEvent();
        },
        [VtoConstants.VtoEvents.SHADE_FINDER_CANCELLED]: () =>
            emit(events.VTO_SHADE_FINDER_CANCELLED, { vtoType: this.vtoType }),
        [VtoConstants.VtoEvents.ZOOM]: () => emit(events.VTO_ZOOM, { vtoType: this.vtoType }),
        [VtoConstants.VtoEvents.SPLIT_FOUR_WAY]: () =>
            emit(events.VTO_SPLIT_FOUR_WAY_ENABLED, { vtoType: this.vtoType }),
        [VtoConstants.VtoEvents.SPLIT_FOUR_WAY_DISABLED]: () =>
            emit(events.VTO_SPLIT_FOUR_WAY_DISABLED, { vtoType: this.vtoType })
    };

    private handleCameraOpenedEvent = () => {
        this.payloadEventValueCameraStart = performance.now();
        emit(events.VTO_CAMERA_OPENED, { vtoType: this.vtoType });
    };

    private handleVtoOpenedEvent = () => {
        if (this.isVtoFoundationEnabled && this.isVtoFoundationOverlayEnabled) {
            this.closeVtoFoundationOverlay();
        }
        if (this.isVtoMakeupEnabled && !this.showProductImageSection) {
            this.enableProductImage(false);
        }
        this.toggleBodyClasses(true);
        this.setIsOpenedState(true);
        window.addEventListener('resize', this.reInitVto);
        this.enableSpinner(false);
        this.setSurveyStorage();
    };

    private handleVtoLoadedEvent = () => {
        this.payloadEventValueCameraEnd = performance.now();

        const { skuId } = this.selectedSku;

        emit(events.VTO_LOADED, {
            vtoType: this.vtoType,
            eventValue: Math.floor(
                this.payloadEventValueCameraEnd - this.payloadEventValueCameraStart
            )
        });

        this.updateVtoLoadedState(true);

        if (this.isVtoMakeupEnabled) {
            this.toggleVtoShadeGrid(true);
            window.YMK.applyMakeupBySku(skuId);
        }
    };

    private handleVtoLoadingEvent = (percentage: IVtoEventListenerResult) => {
        // @ts-ignore
        if (percentage === VtoConstants.LOADING_PERCENTAGE_EVENT_TRIGGERED) {
            emit(events.VTO_LOADING, { vtoType: this.vtoType });
        }
    };

    private handleVtoShadeFinderStartedEvent = () => {
        const isShadeGridEnabled = false;
        emit(events.VTO_SHADE_FINDER_STARTED, { vtoType: this.vtoType });
        this.toggleVtoShadeGrid(isShadeGridEnabled);
    };

    private handleShadeFinderCompleteEvent = () => {
        this.adjustVtoHeight();
    };

    private handleVtoSkinShadeFoundEvent = (result: IVtoEventListenerResult) => {
        this.vtoShadeResult = result.readableRecommendations && result.readableRecommendations[0];
        this.setSaveMatch(false);
        emit(events.VTO_SKIN_SHADE_FOUND, { vtoType: this.vtoType });
        this.foundationShadeFilter();
        this.toggleVtoShadeGrid(true);
        if (this.product.perfectShades.length) {
            this.togglePerfectShades && this.togglePerfectShades(true);
            this.selectPerfectSku(this.vtoShadeResult);
        } else {
            const shades = this.product.shades;
            const skuId = shades.length ? shades[0].parentSku?.skuId : '';
            this.activeTab = VtoConstants.foundationTabs.allShadesTab;
            this.togglePerfectShades && this.togglePerfectShades(false);
            skuId && this.selectFirstSku(skuId);
        }
    };

    private adjustVtoHeight = () => {
        const iframeCtn = this.vtoIframeRef?.current;
        
        if (this.isMobile() && iframeCtn) {
            const iframe: HTMLObjectElement | null = iframeCtn.querySelector('#YMK-module-iframe');

            if (iframe) {
                const intersectionObserver = new IntersectionObserver((entries) => {
                    if (entries[0].intersectionRatio <= 0.95) {
                        const innerCtn = iframe.contentDocument?.body.querySelector('#all-page');
                        
                        if (innerCtn) {
                            iframe.style.height = '100dvh';
                            (innerCtn as HTMLElement).style.height = '100%';
                            intersectionObserver.disconnect();
                        }
                    }
                }, {threshold: 0.95});

                intersectionObserver.observe(iframe);
            }
            
        }
    };

    private closeVto = () => {
        emit(events.VTO_CLOSED, { vtoType: this.vtoType });
        this.toggleBodyClasses(false);
        this.enableProductImage(true);
        this.toggleVtoShadeGrid(false);
        this.resetShadeProps();
        this.togglePerfectShades && this.togglePerfectShades(false);
        this.updateVtoLoadedState(false);
        this.setIsOpenedState(false);
        window.removeEventListener('resize', this.reInitVto);

        if (this.isVtoDisclosed) {
            emit(events.VTO_OPENED, { vtoType: this.vtoType });
        }
        if (!this.isSurveySubmitted && this.hasSurvey) {
            emit(events.VTO_SURVEY_IGNORED, { vtoType: this.vtoType });
        }
        this.enableSidebarImage(true);
    };

    private handleVtoClosedEvent = () => {
        if (this.isSurveyViewable) {
            this.showSurvey();

            return;
        }

        this.closeVto();
    };

    private closeVtoFoundationOverlay = () => {
        this.updateVtoFoundationOverlayState(false);
    };

    @action
    public toggleVtoShadeGrid = (isEnabled: boolean) => {
        this.isVtoShadeGridEnabled = isEnabled;
    };

    @action
    public updateVtoLoadedState = (isLoaded: boolean) => {
        this.isVtoLoaded = isLoaded;
    };

    @action
    public setIsSpinnerEnabled = (isEnabled: boolean) => {
        this.isSpinnerEnabled = isEnabled;
    };

    @action
    public setActiveTab = (activeTab: string) => {
        this.activeTab = activeTab;
    };

    @action
    public onVtoTabClick = (event: React.MouseEvent<HTMLElement>) => {
        const {
            foundationTabs: { perfectShadesTab, allShadesTab, pairShadesTab }
        } = VtoConstants;
        const target = <HTMLElement>event.target;
        this.activeTab = target.dataset.tab || '';

        if (this.activeTab === perfectShadesTab && this.togglePerfectShades) {
            this.togglePerfectShades(true);
            this.handleShadesViewed(VtoConstants.VtoButtonTypes.BEST_SHADES);
        } else if (this.activeTab === pairShadesTab) {
            emit(events.VTO_PAIR_SHADE_GRID_OPENED, { vtoType: this.vtoType });
        } else if (this.activeTab === allShadesTab && this.togglePerfectShades) {
            this.togglePerfectShades(false);
            this.handleShadesViewed(VtoConstants.VtoButtonTypes.ALL_SHADES);
        }
    };

    @on(events.VTO_PAIR_SHADE_GRID_OPENED)
    public setDefaultCrossSellShade = () => {
        if (this.selectedCrossSellShades) {
            const defaultShade = Object.values(this.selectedCrossSellShades)[0].name;
            this.onCrossSellShadeSelect(defaultShade);
        }
    };

    @action
    public setSelectedPairSkuId = (skuId: string) => {
        this.selectedPairSkuId = skuId;
    };

    @action
    public setSelectedPairShadeName = (shadeName: string) => {
        this.selectedPairShadeName = shadeName;
    };

    @computed
    get activeTabContent() {
        const {
            foundationTabs: { perfectShadesTab, allShadesTab, pairShadesTab }
        } = VtoConstants;
        const perfectShades = this.product.perfectShades || [];

        return {
            perfectShades: { active: this.activeTab === perfectShadesTab, disabled: !perfectShades.length },
            pairShades: {
                active: this.activeTab === pairShadesTab,
                disabled: !Object.values(this.selectedCrossSellShades).length
            },
            allShades: { active: this.activeTab === allShadesTab, disabled: false }
        };
    }

    @computed
    get isVtoFoundationOverlayEnabled() {
        return this.vtoFoundationFeature && this.vtoFoundationFeature.isOverlayEnabled;
    }

    @computed
    get isVtoFoundationMultiIdsEnabled() {
        return this.vtoFoundationFeature && this.vtoFoundationFeature.isMultiIdsEnabled;
    }

    get slidesToShow() {
        if (
            this.isVtoFoundationEnabled &&
            this.vtoFoundationFeature &&
            this.vtoFoundationFeature.slidesToShow
        ) {
            return this.vtoFoundationFeature.slidesToShow;
        }
        if (
            this.isVtoMakeupEnabled &&
            this.vtoMakeupFeature &&
            this.vtoMakeupFeature.slidesToShow
        ) {
            return this.vtoMakeupFeature.slidesToShow;
        }

        return VtoConstants.CUSTOM_SLIDES_TO_SHOW;
    }

    get slidesToScroll() {
        if (
            this.isVtoFoundationEnabled &&
            this.vtoFoundationFeature &&
            this.vtoFoundationFeature.slidesToScroll
        ) {
            return this.vtoFoundationFeature.slidesToScroll;
        }
        if (
            this.isVtoMakeupEnabled &&
            this.vtoMakeupFeature &&
            this.vtoMakeupFeature.slidesToScroll
        ) {
            return this.vtoMakeupFeature.slidesToScroll;
        }

        return VtoConstants.CUSTOM_SLIDES_TO_SCROLL;
    }

    public sortPerfectLabels = (a: IShade, b: IShade) => {
        const orderedPerfectLabels = [
            this.translations.coolerLabel,
            this.translations.lighterLabel,
            this.translations.bestLabel,
            this.translations.darkerLabel,
            this.translations.warmerLabel
        ];

        if (a.perfectShadeLabel && b.perfectShadeLabel && orderedPerfectLabels) {
            const labelA = orderedPerfectLabels.indexOf(a.perfectShadeLabel);
            const labelB = orderedPerfectLabels.indexOf(b.perfectShadeLabel);

            if (labelA < labelB) {
                return -1;
            }

            if (labelA > labelB) {
                return 1;
            }
        }

        return 0;
    };

    @computed
    public get selectedCrossSellSku() {
        if (!this.crossSellSkuData) {
            return;
        }
        if (this.selectedPairSkuId) {
            return this.crossSellSkuData.find((sku) => sku.skuId === this.selectedPairSkuId);
        }
        const defaultSkuData = this.crossSellSkuData[0];

        return defaultSkuData?.parentProduct.skus.find(
            (sku) => sku.crossSellCategory && sku.crossSellCategory.length > 0
        );
    }

    @computed
    public get selectedCrossSellProductUrl() {
        if (this.selectedCrossSellSku) {
            const productUrl = this.selectedCrossSellSku.parentProduct.productUrl;
            const selectedShadeName = this.selectedCrossSellSku.shades[0].name;

            return `${productUrl}?shade=${encodeURIComponent(selectedShadeName)}`;
        }

        return '';
    }

    @computed
    public get selectedCrossSellSizeValue() {
        if (this.selectedCrossSellSku) {
            return this.selectedCrossSellSku.sizes && this.selectedCrossSellSku.sizes[0].value;
        }

        return '';
    }

    @computed
    public get selectedCrossSellLargeImages() {
        if (this.selectedCrossSellSku) {
            return this.selectedCrossSellSku.media.large.concat(
                this.selectedCrossSellSku.parentProduct.media.large
            );
        }

        return [];
    }

    @computed
    public get foundationPairText() {
        return Object.values(VtoConstants.FoundationPairShadeTypes).reduce((acc, value) => {
            const key = `${value}Label` as keyof IVtoTranslations;

            return {
                ...acc,
                [value]: this.translations[key]
            };
        }, {} as IVtoFoundationPairShadeLabels);
    }

    @computed
    public get selectedCrossSellShade() {
        return this.selectedCrossSellSku?.shades.find(
            (shade) => shade.name === this.selectedPairShadeName
        );
    }

    @computed
    public get selectedCrossSellShades() {
        const selectedCrossSell = this.selectedSku.crossSellCategory || [];
        const shadeTypes = Object.values(VtoConstants.FoundationPairShadeTypes);
        let shades: ICrossSellShades = {};
        if (this.crossSellSkuData) {
            shades = selectedCrossSell.reduce(
                (acc: ICrossSellShades, value: ICrossSellCategory) => {
                    const crossSellSku = this.crossSellSkuData.find(
                        (sku) => sku.skuId === value.skuId
                    );
                    if (crossSellSku) {
                        acc[shadeTypes[value.position]] = crossSellSku.shades[0];
                    }

                    return acc;
                },
                {}
            );

            return shades;
        }

        return shades;
    }

    private loadCrossSellProducts = async () => {
        const crossSellSkuIds = [
            ...new Set(
                this.selectedSku.parentProduct.skus.flatMap((sku) =>
                    sku.crossSellCategory?.map((category) => category.skuId)
                )
            )
        ].filter((skuId) => skuId) as string[];

        if (crossSellSkuIds.length > 0) {
            try {
                const crossSellProductData = <IProduct[]>(
                    await query(serviceQueries.GET_PRODUCT_DATA, {
                        filterBy: { skus: { skuIds: crossSellSkuIds } }
                    })
                );
                const crossSellProductSkus = crossSellProductData.flatMap(
                    (product) => product.skus
                );
                this.crossSellSkuData = crossSellSkuIds
                    .map((skuId) => crossSellProductSkus.find((sku) => sku.skuId === skuId))
                    .filter((sku) => sku) as ISku[];
            } catch (error) {
                this.logger.error({
                    triggerType: TriggerType.runtimeError,
                    message: `${error}`
                });
            }
        }
    };

    private foundationShadeFilter = () => {
        if (this.vtoShadeResult) {
            const perfectMatches = this.filterPerfectMatches(this.vtoShadeResult);
            this.updateShadeEntity(perfectMatches);
        }
    };

    private reInitVto = () => {
        if (this.vtoReInitTimeout) {
            clearTimeout(this.vtoReInitTimeout);
        }

        this.vtoReInitTimeout = setTimeout(() => {
            const { iframeWidth } = this.getIframeDimension();
            const vtoIframeRef = this.vtoIframeRef && this.vtoIframeRef.current;
            if (iframeWidth !== this.iframeWidth && vtoIframeRef && vtoIframeRef.hasChildNodes()) {
                window.YMK.closeEngine();
                window.YMK.close();
                emit(events.VTO_OPENED, { vtoType: this.vtoType });
            }
        }, VtoConstants.TIMEOUT_VALUE);
    };

    @on(events.VTO_OPENED)
    public initVto = () => {
        if (this.isVtoFoundationSupportedBrowserOverlayEnabled) {
            this.showSupportedBrowserOverlay();

            return;
        }
        if (!this.getVtoApiConfig) {
            return;
        }
        if (typeof window.ymkAsyncInit !== 'function') {
            const { apiUrl, apiKey } = this.getVtoApiConfig;
            this.loadVto(apiUrl, apiKey);
            this.declareVtoCallback();
        }
        this.openVto();
        this.enableSidebarImage(false);
        this.enableSpinner(true);
        this.setIsVtoFoundationQrCodeOverlayEnabled(false);
    };

    private declareVtoCallback = () => {
        window.ymkAsyncInit = () => {
            if (typeof window.YMK !== 'undefined') {
                const vtoParams = this.getVtoParams();
                window.YMK.init(vtoParams);
                this.openVtoFoundation();
                this.addEventListeners(VtoConstants.VtoEvents, this.handleEventListenerCallbacks);
            }
        };
    };

    private openVtoFoundation() {
        if (this.isVtoFoundationEnabled) {
            this.isVtoFoundationOverlayEnabled
                ? this.showVtoFoundationOverlay()
                : window.YMK.open(true, null, true);
        }
    }

    private openVto = () => {
        this.toggleBodyClasses(true);
        window.ymkAsyncInit();
    };

    private showVtoFoundationOverlay = () => {
        this.updateVtoFoundationOverlayState(true);
    };

    private openShadeSidebarVto() {
        if (this.hasShadeSidebarVto && !this.isShadeSidebarVtoOpened) {
            const { VTO_FOUNDATION_QR_CODE_OVERLAY_OPENED, VTO_OPENED } = events;

            this.isShadeSidebarVtoOpened = true;

            VtoBaseController.isFoundationQrCodeOverlayOpen
                ? emit(VTO_FOUNDATION_QR_CODE_OVERLAY_OPENED, { vtoType: this.vtoType })
                : emit(VTO_OPENED, { vtoType: this.vtoType });
        }
    }

    @action
    public updateVtoFoundationOverlayState = (isOverlayOpened: boolean) => {
        this.isVtoFoundationOverlayOpened = isOverlayOpened;
    };

    public handleVtoUnmount = () => {
        this.handleVtoClosedEvent();
        this.removeVtoEventListeners();
    };

    @action
    public onCrossSellShadeSelect = (shadeName: string) => {
        const crossSellSku = Object.values(this.crossSellSkuData).find((sku) =>
            sku.shades.some((shade) => shade.name === shadeName)
        );

        if (crossSellSku) {
            const selectedCrossSellShade = crossSellSku.shades.find(
                (shade) => shade.name === shadeName
            );

            if (this.isVtoLoaded && selectedCrossSellShade) {
                this.setSelectedPairSkuId(crossSellSku.skuId);
                this.setSelectedPairShadeName(selectedCrossSellShade.name);
                emit(events.VTO_PAIR_SHADE_SELECTED, { vtoType: this.vtoType });
                window.YMK.applyMakeupBySku(crossSellSku.skuId);
            }
        }
    };

    @on(events.PRODUCT_SKU_SELECTED)
    public vtoMakeupApply = (payload: { skuId: string }) => {
        if (this.isVtoLoaded) {
            window.YMK.applyMakeupBySku(payload.skuId);
        }
    };

    public handleShadeSelect = (shadeName: string, eventLabel: string) => {
        emit(events.PRODUCT_SHADE_SELECTED, { shadeName });
        emit(events.VTO_SHADE_SELECTED, {
            vtoType: this.getVtoType(),
            eventLabel
        });
    };

    public handleShadesViewed = (buttonType: VtoConstants.VtoButtonTypes) => {
        const { VtoButtonTypes } = VtoConstants;
        const eventByButtonType = {
            [VtoButtonTypes.ALL_SHADES]: events.VTO_ALL_SHADES_VIEWED,
            [VtoButtonTypes.BEST_SHADES]: events.VTO_BEST_SHADES_VIEWED
        };

        !!eventByButtonType[buttonType] &&
            emit(eventByButtonType[buttonType], { vtoType: this.vtoType });
    };

    @on(events.VTO_FOUNDATION_OVERLAY_TOGGLED)
    public vtoFoundationOverlayToggled = (payload: { isOverlayOpened: boolean }) => {
        this.updateVtoFoundationOverlayState(payload.isOverlayOpened);
        emit(events.VTO_CLOSED, { vtoType: this.vtoType });
        this.toggleBodyClasses(false);
    };

    @on(events.SHADE_SIDEBAR_CLOSED)
    public shadeSidebarClosed = () => {
        this.hasShadeSidebarVto && this.handleVtoClosedEvent();
    };

    @action
    public updateProductData = (sku: ISku) => {
        this.selectedSku = sku;
        this.product = sku.parentProduct;
    };

    @action
    public setSaveMatch = (isSaved: boolean) => {
        this.isMatchSaved = isSaved;
    };

    @action
    public setIsSaveInProgress = (isInProgress: boolean) => {
        this.isSaveInProgress = isInProgress;
    };

    @action
    public resetBestShades = () => {
        this.product.skus.forEach((sku) => {
            sku.shades.forEach((shade) => shade.resetBestShade());
        });
    };

    public saveMatches = async () => {
        const recommendations = this.getShadeRecommendations();
        const { productId } = this.product;
        const { skuId } = this.selectedSku;

        const shadeMatches = {
            productId,
            userSelectedMatch: skuId,
            recommendations
        };

        this.setIsSaveInProgress(true);
        this.resetBestShades();
        this.selectedSku.selectedShade.setBestShade();

        setVtoMatchCookie({
            shadeMatches: { [productId]: shadeMatches },
            shouldOverride: true
        });

        await this.personalizeVtoMatches(shadeMatches);

        this.setSaveMatch(true);
        this.setIsSaveInProgress(false);
        this.savedMatch = this.selectedSku.shades[0].name;

        emit(events.VTO_MATCH_SAVED, { vtoType: this.vtoType });
    };

    private getShadeRecommendations() {
        if (!this.vtoShadeResult) {
            return null;
        }
        const shadeTypes = VtoConstants.FoundationShadeTypes;
        const shadeTypeValues = Object.values(shadeTypes);

        return Object.entries(this.vtoShadeResult).reduce((acc, [type, skuId]) => {
            const matchType =
                type === VtoConstants.VTO_GUID
                    ? shadeTypes.BEST
                    : (type as VtoConstants.FoundationShadeTypes);

            if (shadeTypeValues.indexOf(matchType) > -1) {
                return { ...acc, [matchType]: skuId };
            }

            return acc;
        }, {} as IVtoFoundationPerfectMatches);
    }

    private async personalizeVtoMatches(shadeMatches: IVtoFoundationSaveMatch) {
        const userId = await this.userInfoRepository.getUserId();

        if (!userId) {
            return;
        }

        const { VTO_FOUNDATION, PERLGEM_USER_IDENTIFIER_TYPE, VTO_MATCHES, VTO } = VtoConstants;

        const data = {
            userPersonalizationData: JSON.stringify({ [VTO_MATCHES]: shadeMatches }),
            tags: [VTO, VTO_FOUNDATION],
            userIdentifier: userId.toString(),
            userIdentifierType: PERLGEM_USER_IDENTIFIER_TYPE
        };

        await this.personalizationRepository.createPersonalizationData(data);

        emit(events.VTO_MATCH_PERSONALIZED, { vtoType: this.vtoType });
    }

    public removeMatches = () => {
        const cookieName = VtoConstants.VTO_MATCHES_COOKIE_NAME;
        const shadeMatchCookie = getVtoMatchCookie();
        if (!shadeMatchCookie) {
            return;
        }

        const shadeMatches = JSON.parse(shadeMatchCookie);

        shadeMatches[this.product.productId] = {};

        BrowserStorage.setItem(cookieName, JSON.stringify(shadeMatches));

        this.setSaveMatch(false);
        this.savedMatch = '';
        this.selectedSku.selectedShade.resetBestShade();

        emit(events.VTO_MATCH_REMOVED, { vtoType: this.vtoType });
    };

    private enableProductImage(isEnabled: boolean) {
        emit(events.PRODUCT_IMAGE_ENABLED, isEnabled);
    }

    private enableSpinner(isEnabled: boolean) {
        const isSpinnerEnabled =
            isEnabled && (this.isVtoMakeupEnabled || !this.isVtoFoundationOverlayEnabled);

        this.setIsSpinnerEnabled(isSpinnerEnabled);
    }

    private enableSidebarImage(isEnabled: boolean) {
        if (this.hasShadeSidebarVto) {
            emit(events.SHADE_SIDEBAR_IMAGE_ENABLED, { isEnabled });
        }
    }

    get isVtoFoundationSupportedBrowserOverlayEnabled() {
        return (
            this.isVtoFoundationEnabled &&
            this.vtoFoundationFeature?.isSupportedBrowserMsgEnabled &&
            this.isSupportedBrowserUserAgent
        );
    }

    @action
    public setIsVtoFoundationQrCodeOverlayEnabled = (isEnabled: boolean) => {
        this.isVtoFoundationQrCodeOverlayEnabled = isEnabled;
    };

    @on(events.VTO_FOUNDATION_QR_CODE_OVERLAY_CLOSED)
    public handleVtoFoundationQrCodeOverlayClose = () => {
        this.setIsVtoFoundationQrCodeOverlayEnabled(false);
        this.handleVtoClosedEvent();
    };

    @on(events.VTO_FOUNDATION_QR_CODE_OVERLAY_OPENED)
    public handleQrCodeFoundationOverlay = () => {
        this.setIsVtoFoundationQrCodeOverlayEnabled(true);
        this.setIsOpenedState(true);
        this.enableProductImage(false);
        this.enableSidebarImage(false);
        this.enableSpinner(false);
    };

    @action
    public setIsSurveyVisible = (isVisible: boolean) => {
        this.isSurveyVisible = isVisible;
    };

    @action
    public closeSurvey = () => {
        this.setIsSurveyVisible(false);
        this.surveyViewed = true;
        this.setThankMessageStatus(false);
        this.surveyButtonIndex = null;
        this.setSurveyComment('');
        this.closeVto();
    };

    public submitSurveyData = async ({ rating, comment = '' }: ISurveyFields) => {
        setVtoSurveyStorage({ vtoType: this.vtoType, data: { isSurveyTaken: true } });

        const userData = await this.userInfoRepository.getUser();
        const { userId, sessionId } = userData;
        const id = userId || sessionId;

        if (!id) {
            return;
        }

        const userIdType = userId ? PERLGEM_USER_ID : SESSION_ID;
        const surveyType = surveyTypes[this.vtoType as VtoConstants.VtoTypes];
        const personalizationData = {
            [surveyType]: {
                rating,
                comment,
                userIdType
            }
        };

        const data = {
            userPersonalizationData: JSON.stringify(personalizationData),
            tags: [surveyType],
            userIdentifier: id.toString(),
            userIdentifierType: VtoConstants.PERLGEM_USER_IDENTIFIER_TYPE
        };

        await this.personalizationRepository.createPersonalizationData(data);

        const surveyData: ISurveyData = {
            np: rating?.toString(),
            comment,
            vtoType: this.vtoType,
            userIdentifier: id,
            page: window.location.href
        };

        await this.surveyRepository.saveSurveyData(surveyData);
    };

    public handleSurveySubmit = ({ rating, comment }: ISurveyFields) => {
        this.setThankMessageStatus(true);
        setTimeout(this.closeSurvey, CLOSE_SURVEY_TIMEOUT);
        this.submitSurveyData({ rating, comment });
        emit(events.VTO_SURVEY_SUBMITTED, { vtoType: this.vtoType });
        this.isSurveySubmitted = true;
        this.setSurveyComment('');
    };

    @computed
    get isSurveyViewable() {
        return this.isVtoLoaded && this.hasSurvey && !this.isSurveyTaken;
    }

    get hasSurvey() {
        if (this.isVtoMakeupEnabled && !this.disableSurvey) {
            return this.vtoFeature?.makeup?.hasSurvey;
        } else if (this.isVtoFoundationEnabled) {
            return this.vtoFeature?.foundation?.hasSurvey;
        }
    }
    get isSurveyTaken() {
        const surveyStorage = getVtoSurveyStorage(this.vtoType);

        if (surveyStorage?.isSurveyTaken || this.surveyViewed) {
            return true;
        }
    }

    get disableSurvey() {
        const sppContainer = document.getElementsByClassName('js-spp-content');
        const surveyDisabled = sppContainer[0]?.getAttribute('data-disable-survey');
        if (this.vtoFeature?.makeup?.makeupVariant && surveyDisabled === 'true') {
            return true;
        }

        return false;
    }

    private showSurvey() {
        this.setIsSurveyVisible(true);
        emit(events.VTO_SURVEY_LOADED, { vtoType: this.vtoType });
    }

    private async setSurveyStorage() {
        const surveyUseCase = new SurveyUseCase({ vtoType: this.vtoType });

        await surveyUseCase.execute();
    }

    @action
    public setThankMessageStatus = (status: boolean) => {
        this.showThankMessage = status;
    };

    @action
    public setSurveyButtonIndex = (index: number) => {
        this.surveyButtonIndex = index;
    };

    @action
    public setSurveyComment = (surveyComment: string) => {
        this.surveyComment = surveyComment;
    };
}
